import React, { useEffect, useRef, useState } from "react";
import { InboxOutlined, InfoCircleOutlined } from "@ant-design/icons";
import {
  Upload,
  Button,
  Card,
  Typography,
  Space,
  Modal,
  Progress,
  message,
  Select,
} from "antd";
import AIService from "../services/AIService";
import { showErrorWithCopy } from "../utils/errorNotice.jsx";
import { recordTiming, recordError } from "../services/modelStats";
import { usePasteFiles } from "../hooks/usePasteFiles";
import { emitGenerationComplete } from "../services/generationNotifications";
import {
  ANALYSIS_MODEL_OPTIONS,
  DEFAULT_ANALYSIS_MODEL,
  GEMINI_VISION_MODEL,
  normalizeModelSelection,
} from "../services/modelCatalog";

const { Text } = Typography;

const UploadPage = ({
  onAnalysisComplete,
  onAnalysisProgress,
  showCard = true,
  apiKeys,
  analysisModel,
  onAnalysisModelChange,
  workspaceId,
}) => {
  const [fileList, setFileList] = useState([]);
  const [analyzing, setAnalyzing] = useState(false);
  const [infoOpen, setInfoOpen] = useState(false);
  const [analysisStage, setAnalysisStage] = useState(null);
  const abortRef = useRef(null);
  const cancelledRef = useRef(false);
  const lastProgressTickRef = useRef(Date.now());
  const lastProgressPercentRef = useRef(0);
  const lastProgressLabelRef = useRef("");
  const finalizeTimerRef = useRef(null);
  const pasteRef = useRef(null);
  const [displayProgress, setDisplayProgress] = useState({
    percent: 0,
    label: "",
  });
  const [messageApi, contextHolder] = message.useMessage();
  const resolvedAnalysisModel = normalizeModelSelection(
    analysisModel,
    DEFAULT_ANALYSIS_MODEL,
  );

  const resolveStageInfoFrom = (stageValue) => {
    if (!stageValue) return null;
    if (typeof stageValue === "string") {
      return { stage: stageValue, segment: null, total: null };
    }
    if (typeof stageValue === "object") {
      return {
        stage: stageValue.stage || "segmenting",
        segment: stageValue.segment || null,
        total: stageValue.total || null,
      };
    }
    return null;
  };

  const pushProgress = (next, active = true) => {
    if (!next) return;
    const prevPercent = lastProgressPercentRef.current;
    const prevLabel = lastProgressLabelRef.current;
    if (next.percent !== prevPercent || next.label !== prevLabel) {
      lastProgressTickRef.current = Date.now();
    }
    lastProgressPercentRef.current = next.percent;
    lastProgressLabelRef.current = next.label;
    setDisplayProgress(next);
    onAnalysisProgress?.({
      active,
      percent: next.percent,
      label: next.label,
    });
  };

  const finalizeProgress = (labelOverride) => {
    if (finalizeTimerRef.current) {
      clearTimeout(finalizeTimerRef.current);
    }
    const label = labelOverride || "Finalizing";
    pushProgress({ percent: 100, label }, true);
    finalizeTimerRef.current = setTimeout(() => {
      onAnalysisProgress?.({ active: false, percent: 100, label });
      setDisplayProgress({ percent: 0, label: "" });
    }, 400);
  };

  const emitProgress = (stageValue, active = true) => {
    const info = resolveStageInfoFrom(stageValue);
    if (!info) {
      if (!active) {
        onAnalysisProgress?.({ active: false, percent: 0, label: "" });
        setDisplayProgress({ percent: 0, label: "" });
      }
      return;
    }
    const label = stageLabels[info.stage] || "Analyzing";
    const segmentPrefix =
      info.segment && info.total ? `Segment ${info.segment}/${info.total} - ` : "";
    const basePercent = stagePercent[info.stage] || 0;
    const percent =
      info.segment && info.total
        ? Math.min(
            100,
            Math.round(
              ((info.segment - 1) / info.total) * 100 +
                basePercent / info.total,
            ),
          )
        : basePercent;
    const isTerminal = info.stage === "complete" || info.stage === "cancelled";
    const cappedPercent = isTerminal ? 100 : Math.min(99, percent);
    pushProgress(
      {
        percent: cappedPercent,
        label: `${segmentPrefix}${label}`.trim(),
      },
      active,
    );
  };

  const handleUpload = async () => {
    if (fileList.length === 0) {
      messageApi.error("Please upload a file first!");
      return;
    }

    cancelledRef.current = false;
    if (finalizeTimerRef.current) {
      clearTimeout(finalizeTimerRef.current);
      finalizeTimerRef.current = null;
    }
    setDisplayProgress({ percent: 0, label: "" });
    lastProgressPercentRef.current = 0;
    lastProgressLabelRef.current = "";
    lastProgressTickRef.current = Date.now();
    setAnalyzing(true);
    emitProgress("starting", true);
    const file = fileList[0];
    const isVideo = file.type.startsWith("video/");
    const controller = new AbortController();
    abortRef.current = controller;

    const analysisStart = performance.now();
    const modelToUse = isVideo ? GEMINI_VISION_MODEL : resolvedAnalysisModel;
    const modelLabel = modelToUse;
    let finalized = false;
    try {
      const keys = apiKeys || {};
      const aiService = new AIService({
        ...keys,
        aiProfile: keys.aiProfile,
        analysisKnowledge: keys.analysisKnowledge,
        adCopyKnowledge: keys.adCopyKnowledge,
        tagProfile: keys.tagProfile,
        generationAspectRatio: keys.generationAspectRatio,
        workspaceId,
      });
      const initialStage = isVideo
        ? { stage: "segmenting", segment: 1, total: 1 }
        : "image";
      setAnalysisStage(initialStage);
      emitProgress(initialStage, true);
      const rawAnalysis = await aiService.analyzeCreative(
        file,
        isVideo,
        (stage) => {
          const nextStage = stage || (isVideo ? "segmenting" : "image");
          setAnalysisStage(nextStage);
          emitProgress(nextStage, true);
        },
        controller.signal,
        { model: modelToUse },
      );
      const analysisMs = performance.now() - analysisStart;
      recordTiming({
        category: "analysis",
        model: modelLabel,
        ms: analysisMs,
        ok: true,
      });
      if (cancelledRef.current) {
        return;
      }
      const previewUrl = URL.createObjectURL(file);
      // Durable, downscaled copy of the source image so it can always be shipped
      // as a reference later (the blob: previewUrl above doesn't survive a reload).
      let previewDataUrl = "";
      if (!isVideo) {
        try {
          const prepared = await aiService.prepareOpenAIImage(file, {
            maxDim: 1024,
            quality: 0.85,
          });
          if (prepared?.base64) {
            previewDataUrl = `data:${prepared.mimeType || "image/jpeg"};base64,${prepared.base64}`;
          }
        } catch (prepErr) {
          // non-fatal — fall back to the blob previewUrl for referencing
        }
      }
      const masterContext = isVideo
        ? rawAnalysis?.segments
          ? rawAnalysis
          : rawAnalysis.combined_context || rawAnalysis
        : rawAnalysis;

      messageApi.success("Analysis complete!");
      emitGenerationComplete({
        source: "analysis",
        title: "Analysis complete",
        body: `${file.name || "Creative"} is ready.`,
        success: true,
      });
      finalizeProgress("Finalizing");
      finalized = true;
      onAnalysisComplete({
        type: isVideo ? "video" : "image",
        previewUrl,
        previewDataUrl,
        fileName: file.name,
        analysis: rawAnalysis,
        masterContext,
      });
    } catch (error) {
      if (error?.name === "AbortError" || cancelledRef.current) {
        finalizeProgress("Cancelled");
        finalized = true;
        return;
      }
      const analysisMs = performance.now() - analysisStart;
      recordTiming({
        category: "analysis",
        model: modelLabel,
        ms: analysisMs,
        ok: false,
      });
      recordError({ category: "analysis", model: modelLabel });
      console.error(error);
      showErrorWithCopy(
        messageApi,
        "Analysis failed",
        error,
        "UploadPage.handleUpload",
      );
      emitGenerationComplete({
        source: "analysis",
        title: "Analysis failed",
        body: error?.message || "The upload analysis failed.",
        success: false,
      });
      finalizeProgress("Failed");
      finalized = true;
    } finally {
      setAnalyzing(false);
      setAnalysisStage("");
      if (!finalized) {
        finalizeProgress("Finalizing");
      }
      abortRef.current = null;
    }
  };

  const handleCancel = () => {
    if (!analyzing) return;
    cancelledRef.current = true;
    abortRef.current?.abort();
    setAnalyzing(false);
    setAnalysisStage("");
    finalizeProgress("Cancelled");
    messageApi.info("Analysis cancelled.");
  };

  const props = {
    onRemove: () => setFileList([]),
    beforeUpload: (file) => {
      setFileList([file]);
      return false; // Prevent automatic upload
    },
    fileList,
    accept: "image/*,video/*",
  };

  usePasteFiles({
    ref: pasteRef,
    disabled: analyzing,
    global: true,
    allowedTypes: ["image/", "video/"],
    onFiles: (files) => {
      if (!files || files.length === 0) return;
      setFileList([files[0]]);
      messageApi.success(`Pasted ${files[0].name}`);
    },
  });

  const stageLabels = {
    starting: "Starting analysis",
    image: "Analyzing image",
    segmenting: "Segmenting video",
    environment: "Analyzing environment",
    script: "Analyzing script",
    actions: "Analyzing actions",
    complete: "Finalizing",
    cancelled: "Cancelled",
  };
  const stagePercent = {
    starting: 5,
    image: 55,
    segmenting: 10,
    environment: 33,
    script: 66,
    actions: 100,
    complete: 100,
    cancelled: 0,
  };

  const resolveStageInfo = () => resolveStageInfoFrom(analysisStage);
  const showLocalProgress =
    displayProgress.percent > 0 ||
    (analyzing && Boolean(analysisStage));

  useEffect(() => {
    if (!analyzing) return;
    const timer = setInterval(() => {
      const elapsedMs = Date.now() - lastProgressTickRef.current;
      if (elapsedMs < 3000) return;
      const nextPercent = Math.min(99, lastProgressPercentRef.current + 1);
      if (nextPercent <= lastProgressPercentRef.current) return;
      pushProgress(
        {
          percent: nextPercent,
          label: lastProgressLabelRef.current || "Analyzing",
        },
        true,
      );
    }, 1000);
    return () => clearInterval(timer);
  }, [analyzing]);

  const content = (
    <>
      {contextHolder}
      <Space direction="vertical" size="small" style={{ width: "100%" }}>
        <Text type="secondary" style={{ fontSize: 12 }}>
          Drag and drop a file to analyze, or paste with Ctrl+V.
        </Text>

        <div ref={pasteRef} tabIndex={0} className="paste-upload-area">
          <Upload.Dragger {...props} style={{ padding: 12 }}>
            <p className="ant-upload-drag-icon">
              <InboxOutlined />
            </p>
            <p className="ant-upload-text">Drop image or video here</p>
            <p className="ant-upload-hint">JPG, PNG, or common video formats</p>
          </Upload.Dragger>
        </div>

        <Space direction="vertical" size={4} style={{ width: "100%" }}>
          <Text style={{ fontSize: 12 }}>Model</Text>
          <Select
            size="small"
            value={resolvedAnalysisModel}
            onChange={(value) => {
              onAnalysisModelChange?.(value);
            }}
            options={ANALYSIS_MODEL_OPTIONS}
          />
        </Space>

        <Space size="small" style={{ width: "100%" }}>
          <Button
            type="primary"
            icon={<InboxOutlined />}
            loading={analyzing}
            onClick={handleUpload}
            disabled={fileList.length === 0}
            size="small"
          >
            {analyzing ? "Analyzing..." : "Analyze"}
          </Button>
          {analyzing && (
            <Button size="small" onClick={handleCancel}>
              Cancel
            </Button>
          )}
          <Button
            type="text"
            icon={<InfoCircleOutlined />}
            onClick={() => setInfoOpen(true)}
            size="small"
          >
            Tips
          </Button>
        </Space>
        {showLocalProgress && (
          <Space direction="vertical" size={4} style={{ width: "100%" }}>
            <Text style={{ fontSize: 12 }}>
              {displayProgress.label || "Analyzing..."}
            </Text>
            <Progress percent={displayProgress.percent} status="active" />
          </Space>
        )}
      </Space>

      <Modal
        title="Upload Tips"
        open={infoOpen}
        onCancel={() => setInfoOpen(false)}
        footer={null}
      >
        <Space direction="vertical" size="small" style={{ width: "100%" }}>
          <Text>Supported: JPG, PNG, and common video formats.</Text>
          <Text>Keep files under ~50MB for faster analysis.</Text>
          <Text>Clear, high-contrast creatives yield better insights.</Text>
        </Space>
      </Modal>
    </>
  );

  if (!showCard) {
    return content;
  }

  return (
    <Card size="small" title="Upload">
      {content}
    </Card>
  );
};

export default UploadPage;
